import { useCallback, useEffect, useState } from 'react';
import { api, useSocket, useSocketEvent } from '@mobi/web';
import type { LatLng } from '@mobi/shared';

export interface DriverStatus {
  hasProfile: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected' | 'suspended' | null;
  rejectionReason?: string | null;
  cnhExpiresOn?: string;
  missing: string[];
  readyForReview?: boolean;
  canGoOnline?: boolean;
  subscription: { planName: string; expiresOn: string; daysLeft: number } | null;
  subscriptionWarning?: string | null;
  vehicle: {
    id: number;
    type: 'car' | 'moto';
    plate: string;
    model: string;
    color: string;
    year: number;
    hasCrlv: boolean;
  } | null;
}

export interface RideOffer {
  rideId: number;
  expiresInMs: number;
  originAddress: string;
  destinationAddress: string;
  origin: LatLng;
  distanceToPickupMeters: number;
  pickupEtaSeconds: number;
  rideDistanceMeters: number;
  rideDurationSeconds: number;
  fareCents: number;
  vehicleType: 'car' | 'moto';
}

export interface DriverRide {
  id: number;
  status: string;
  /** só vem na listagem: se o motorista já avaliou esta corrida */
  rated?: boolean;
  origin: LatLng & { address: string };
  destination: LatLng & { address: string };
  estimate: { distanceMeters: number; durationSeconds: number; fareCents: number };
  finalFareCents: number | null;
  walletAppliedCents: number;
  cashDueCents: number | null;
  paymentMethod: 'cash' | 'pix' | 'credit' | 'debit' | null;
  routePolyline: string | null;
  passenger: {
    id: number;
    name: string;
    phone: string | null;
    rating: number | null;
    ratingCount: number;
    /** o servidor só marca true enquanto a foto pode ser vista */
    hasPhoto: boolean;
  } | null;
}

export function useDriverStatus() {
  const [status, setStatus] = useState<DriverStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api<DriverStatus>('/api/driver/me'));
    } catch {
      // mantém o último estado conhecido em vez de piscar a tela
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { status, loading, refresh };
}

/**
 * Corrida ativa e oferta pendente do motorista.
 *
 * Igual ao lado do passageiro: o socket avisa, o REST decide. Uma oferta
 * aceita por engano duas vezes, ou um evento que chega fora de ordem, não
 * podem mudar o que está na tela sem o servidor confirmar.
 */
export function useDriverRide(online: boolean) {
  const { socket, connected } = useSocket(true);
  const [ride, setRide] = useState<DriverRide | null>(null);
  const [offer, setOffer] = useState<RideOffer | null>(null);
  const [passengerPosition, setPassengerPosition] = useState<LatLng | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ ride: DriverRide | null }>('/api/rides/active');
      setRide(res.ride);
      if (res.ride) setOffer(null);
      else setPassengerPosition(null);
    } catch {
      // sem rede: segura a tela
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (connected) void refresh();
  }, [connected, refresh]);

  useSocketEvent<RideOffer>(socket, 'ride:offer', (payload) => {
    // Oferta só aparece se o motorista está de fato disponível. Sem esta
    // guarda, quem acabou de ficar offline ainda veria a tela pular.
    if (online) setOffer(payload);
  });

  useSocketEvent<{ rideId: number }>(socket, 'ride:offer_expired', (payload) => {
    setOffer((current) => (current?.rideId === payload.rideId ? null : current));
  });

  useSocketEvent(socket, 'ride:status', () => void refresh());
  useSocketEvent(socket, 'ride:rejoined', () => void refresh());

  useSocketEvent<{ lat: number; lng: number }>(socket, 'ride:passenger_position', (p) =>
    setPassengerPosition({ lat: p.lat, lng: p.lng }),
  );

  return {
    socket,
    connected,
    ride,
    offer,
    passengerPosition,
    loading,
    refresh,
    dismissOffer: () => setOffer(null),
  };
}
